import React, { useEffect, useState, ChangeEvent, useMemo } from 'react';
import LateralPanel from '../../../../muiComponents/LateralPanel';
import Title from '../../yourProperties/Title';
import Tag from '../../../../muiComponents/Tag';
import Tooltip from '../../../../muiComponents/Tooltip';
import SwitchInput from '../../../../muiComponents/switchInput/SwitchInput';
import Icon from '../../../../muiComponents/Icon';
import useTranslate from '../../../../customHooks/useTranslate';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import {
    ISupplementComplementaryDataOption,
    ISupplementDetailState,
    SupplementAppliesTo,
} from '../../../../types/supplement/supplementsTypes';
import {
    SUPPLEMENT_INITIAL_STATE,
    SUPPLEMENT_DATE_TAG_COLOR,
    SUPPLEMENT_PANEL_VALIDATION_INITIAL_STATE,
    appliesToOptions,
} from '../../../../utils/supplements/supplementUtils';
import {
    getSupplementData,
    saveSupplementToDDBB,
} from '../../../../services/supplements/supplementsServices';
import { adaptSupplementDetailFromDDBB } from '../../../../services/supplements/supplementAdapters';
import { useSearchParams } from '../../../../customHooks/useSearchParams';
import { useNotification } from '../../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../../utils/commonVariables/messages';
import { isDateless, categorizePeriod } from '../../../../utils/formatDates';
import {
    doubleMD_ColumnGrid,
    doubleLG_ColumnGrid,
    gridSpacing30,
    singleLG_DoubleMD_ColumnGrid,
    full_ColumnGrid,
} from '../../../../utils/commonVariables/grid';
import TextInput from '../../../../muiComponents/TextInput/TextInput';
import DateInput from '../../../../muiComponents/DateInput';
import LoadedOptionsTypeahead from '../../../../muiComponents/LoadedOptionsTypeahead';
import {
    useSupplementsComplementaryData,
    useSupplementsList,
} from '../../../contextsProperty/ContextSupplements';
import { useODataMultipleQueries } from '../../../../customHooks/useODataMultipleQueries';
import {
    readOnlyEntities,
    savableEntities,
} from '../../../../utils/commonVariables/entityKeys';
import IconRadioGroup from '../../../../muiComponents/iconRadioGroup/IconRadioGroup';
import {
    formatSupplierTaxCalculationRuleTypes,
    supplementCalculationTypeOrder,
    supplierTaxCalculationRuleTypeFrequencyIcons,
    supplierTaxCalculationRuleTypeFrequencyOrder,
    supplierTaxCalculationRuleTypeSubjectIcons,
    supplierTaxCalculationTypesOrder,
    UnknownOptionIcons,
} from '../../../../utils/taxes/taxesListUtils';
import SupplementAdditionalInfo from '../../taxes/taxPanel/SupplementAdditionalInfo';
import Button from '../../../../muiComponents/Button';
import convertToOvariant from '../../../../utils/convertToOvariant';
import api from '../../../../services/api';
import endpoints from '../../../../utils/commonVariables/endpoints';
import { validateForm } from '../../../../utils/validateForms';
import LoadingTaxPanel from '../../taxes/LoadingTaxPanel';
import useUserProfile from '../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../ReadOnlyUserAlert';

const FIXED_ID_TAX_CALCULATION_TYPE = 4;
const isFixedTaxId = (id: number) => id === FIXED_ID_TAX_CALCULATION_TYPE;

interface SupplementPanelProps {
    open: boolean;
    onClose: () => void;
}

const SupplementPanel = ({ open, onClose }: SupplementPanelProps) => {
    const translate = useTranslate();
    const { readOnlyUser } = useUserProfile();

    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { getSearchParam, removeSearchParam } = useSearchParams({
        base64: true,
    });
    const {
        supplierHotels,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useSupplementsComplementaryData();
    const { handleSearch } = useSupplementsList();

    const [supplementDetail, setSupplementDetail] =
        useState<ISupplementDetailState>(SUPPLEMENT_INITIAL_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(
        SUPPLEMENT_PANEL_VALIDATION_INITIAL_STATE
    );
    const [ruleTypeAppliesBy, setRuleTypeAppliesBy] = useState<string>('');
    const [ruleTypeFrequency, setRuleTypeFrequency] = useState<string>('');
    const [appliesTo, setAppliesTo] = useState<SupplementAppliesTo>(
        SupplementAppliesTo.ALL
    );

    const idUrl = getSearchParam('id');

    const getSupplementDetail = async (id: string) => {
        try {
            setIsLoading(true);
            const { data } = await getSupplementData(id);
            if (data.Result && data.StatusCode === 200) {
                setSupplementDetail(adaptSupplementDetailFromDDBB(data.Result));
            } else {
                removeSearchParam('id');
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            removeSearchParam('id');
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (idUrl) {
            if (idUrl !== 'new') {
                getSupplementDetail(idUrl);
            }
        }
    }, [idUrl]);

    const handleDetailSupplementChange = (
        updatedFields: Partial<ISupplementDetailState>
    ) => {
        setSupplementDetail(prev => {
            return prev ? { ...prev, ...updatedFields } : prev;
        });
    };

    const isNew = !supplementDetail?.Id || parseInt(supplementDetail.Id) <= 0;
    const datelessFrom = isDateless(supplementDetail?.FromDate);
    const datelessTo = isDateless(supplementDetail?.ToDate);

    const isDatelessSupplement = datelessFrom && datelessTo;
    const tagText = categorizePeriod(
        supplementDetail?.FromDate,
        supplementDetail?.ToDate
    );

    const validateFields = {
        Name: {
            value: supplementDetail?.Name,
            validators: ['requiredString'],
        },
        ToDate: {
            value: [supplementDetail?.FromDate, supplementDetail?.ToDate],
            inputKey: 'ToDate',
            validators: ['fromToDate'],
        },
        IdSupplierHotel: {
            value: supplementDetail?.IdSupplierHotel,
            validators: ['requiredIdEntity'],
        },
        IdSupplierTaxCalculationType: {
            value: supplementDetail?.IdSupplierTaxCalculationType,
            validators: ['requiredIdEntity'],
        },
        AppliesBy: {
            value: ruleTypeAppliesBy,
            validators: ['requiredString'],
        },
        Frequency: {
            value: ruleTypeFrequency,
            validators: ['requiredString'],
        },
        IdSupplementCalculationType: {
            value: supplementDetail?.IdSupplementCalculationType,
            validators: ['requiredIdEntity'],
        },

        ...(isFixedTaxId(supplementDetail?.IdSupplierTaxCalculationType || 0)
            ? {
                  IdCurrency: {
                      value: supplementDetail?.IdCurrency,
                      validators: ['requiredIdEntity'],
                  },
              }
            : {}),
        ...(!isFixedTaxId(supplementDetail?.IdSupplierTaxCalculationType || 0)
            ? {
                  Amount: {
                      value: { value: supplementDetail?.Amount, min: -100 },
                      validators: ['floatGreaterOrEqualThan'],
                  },
              }
            : {}),
    };

    const handleValidateFields = () => {
        setFormErrors(prev => ({
            ...SUPPLEMENT_PANEL_VALIDATION_INITIAL_STATE,
            isValidated: prev.isValidated,
        }));
        if (!formErrors.isValidated) {
            setFormErrors(prev => ({
                ...prev,
                isValidated: true,
            }));
        }
        const errors = validateForm(validateFields);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    const handleDeleteSupplement = async () => {
        if (isNew) {
            onClose();
            handleResetPopUp();
            return;
        }
        if (supplementDetail) {
            try {
                setIsLoading(true);
                const payload = convertToOvariant(
                    savableEntities.SupplierSupplement,
                    {
                        Ids: [supplementDetail.Id],
                    }
                );
                const endpoint = endpoints.commonDeleteItems;
                const res = await api.post(endpoint, payload);

                if (res.data && res.status === 200) {
                    setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_DELETE);
                    handleSearch();
                    onClose();
                    handleResetPopUp();
                } else {
                    setErrorMessage(FeedbackMessages.COMMON_ERROR);
                }
            } catch (error) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const saveDDBBSupplement = async () => {
        const validated = handleValidateFields();
        if (!validated) return;
        try {
            setIsLoading(true);
            const { data } = await saveSupplementToDDBB(supplementDetail);
            if (data) {
                setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                handleSearch();
                onClose();
                handleResetPopUp();
            } else {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPopUp = () => {
        setSupplementDetail(SUPPLEMENT_INITIAL_STATE);
        setFormErrors(SUPPLEMENT_PANEL_VALIDATION_INITIAL_STATE);
        setRuleTypeAppliesBy('');
        setRuleTypeFrequency('');
        setAppliesTo(SupplementAppliesTo.ALL);
    };

    const supplierTaxCalculationRuleTypesOptions = useMemo(() => {
        return formatSupplierTaxCalculationRuleTypes(
            supplierTaxCalculationRuleTypes.data || []
        );
    }, [supplierTaxCalculationRuleTypes]);

    const IdSupplierHotel = supplementDetail?.IdSupplierHotel || null;
    const selectedSupplierHotel = supplierHotels.data?.find(
        hotel => parseInt(hotel.Id) === supplementDetail?.IdSupplierHotel
    );
    const isPerPersonRule = ruleTypeAppliesBy === 'PERSON';

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!IdSupplierHotel,
    });

    useEffect(() => {
        if (supplementDetail?.OnlyChildren) {
            setAppliesTo(SupplementAppliesTo.ONLY_CHILDREN);
        }
        if (supplementDetail?.OnlyAdults) {
            setAppliesTo(SupplementAppliesTo.ONLY_ADULTS);
        }
        if (!supplementDetail?.OnlyAdults && !supplementDetail?.OnlyChildren) {
            setAppliesTo(SupplementAppliesTo.ALL);
        }
    }, [supplementDetail.OnlyAdults, supplementDetail.OnlyChildren]);

    useEffect(() => {
        if (supplementDetail?.IdSupplierTaxCalculationRuleType) {
            const matchingRuleType = supplierTaxCalculationRuleTypes.data?.find(
                ruleType =>
                    parseInt(ruleType.Id) ===
                    supplementDetail.IdSupplierTaxCalculationRuleType
            );

            if (matchingRuleType?.Key) {
                const match = matchingRuleType.Key?.match(
                    /^PER([A-Z]+)PER([A-Z]+)$/
                );
                if (match) {
                    const [, subject, frequency] = match;
                    setRuleTypeAppliesBy(subject.toUpperCase());
                    setRuleTypeFrequency(frequency.toUpperCase());
                }
            }
        }
    }, [
        supplementDetail.IdSupplierTaxCalculationRuleType,
        supplierTaxCalculationRuleTypes,
    ]);

    useEffect(() => {
        if (ruleTypeAppliesBy && ruleTypeFrequency) {
            const matchingRuleType = supplierTaxCalculationRuleTypes.data?.find(
                ruleType =>
                    ruleType.Key?.includes(ruleTypeAppliesBy) &&
                    ruleType.Key?.includes(ruleTypeFrequency)
            );
            if (
                matchingRuleType &&
                matchingRuleType.Id &&
                parseInt(matchingRuleType.Id) !==
                    supplementDetail?.IdSupplierTaxCalculationRuleType
            ) {
                handleDetailSupplementChange({
                    IdSupplierTaxCalculationRuleType: parseInt(
                        matchingRuleType.Id
                    ),
                    EffectivePerson:
                        ruleTypeAppliesBy === 'PERSON'
                            ? supplementDetail?.EffectivePerson || 0
                            : 0,
                });
            }
        }
    }, [ruleTypeAppliesBy, ruleTypeFrequency]);

    return (
        <LateralPanel open={open} className='--xl-width padding-bottom-32'>
            <header className='flex flex-between flex-align-center '>
                <Title
                    text={
                        isNew
                            ? translate('new_supplement')
                            : supplementDetail?.Name
                    }
                    className='text-secondary-color'
                    translated={!supplementDetail?.Name}
                />
                <div className='flex flex-align-center gap-20'>
                    {!isDatelessSupplement && (
                        <Tag
                            text={tagText}
                            color={SUPPLEMENT_DATE_TAG_COLOR[tagText]}
                        />
                    )}
                    <Tooltip
                        title={translate(
                            supplementDetail?.Enabled ? 'enabled' : 'disabled'
                        )}
                    >
                        <div>
                            <SwitchInput
                                disabled={isLoading || readOnlyUser}
                                checked={supplementDetail?.Enabled || false}
                                labelText={''}
                                onChangeMethod={(
                                    _e: ChangeEvent<HTMLInputElement>,
                                    v: boolean
                                ) => {
                                    handleDetailSupplementChange({
                                        Enabled: v,
                                    });
                                }}
                            />
                        </div>
                    </Tooltip>
                    <Icon
                        iconName='Close'
                        className='cursor-pointer'
                        onClick={() =>
                            !isLoading && (onClose(), handleResetPopUp())
                        }
                    />
                </div>
            </header>
            <div className='padding-block-20'>
                <ReadOnlyUserAlert text='read_only_supplements' />
            </div>
            {isLoading ? (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <LoadingTaxPanel />
                </main>
            ) : (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <Grid
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing30}
                        style={{ width: 'auto' }}
                    >
                        <GridChild gridProps={doubleLG_ColumnGrid}>
                            <TextInput
                                required
                                labelText='name'
                                value={supplementDetail?.Name}
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    handleDetailSupplementChange({
                                        Name: e.target.value,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                disabled={readOnlyUser}
                                error={!!formErrors.Name}
                                errorMessage={formErrors.Name}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                required
                                disabled={
                                    supplierHotels.isLoading ||
                                    !isNew ||
                                    readOnlyUser
                                }
                                labelText='hotel'
                                options={supplierHotels.data || []}
                                idValue={supplementDetail?.IdSupplierHotel}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ISupplementComplementaryDataOption | null
                                ) => {
                                    handleDetailSupplementChange({
                                        IdSupplierHotel: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.IdSupplierHotel}
                                errorMessage={formErrors.IdSupplierHotel}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='room'
                                options={supplierHotelRoomTypes.data || []}
                                idValue={
                                    supplementDetail?.IdSupplierHotelRoomType
                                }
                                disabled={
                                    !supplementDetail?.IdSupplierHotel ||
                                    supplierHotelRoomTypes.isLoading ||
                                    readOnlyUser
                                }
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ISupplementComplementaryDataOption | null
                                ) => {
                                    handleDetailSupplementChange({
                                        IdSupplierHotelRoomType: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='meal_plan'
                                disabled={
                                    !supplementDetail?.IdSupplierHotel ||
                                    supplierHotelMealPlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelMealPlans.data || []}
                                idValue={
                                    supplementDetail?.IdSupplierHotelMealPlan
                                }
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ISupplementComplementaryDataOption | null
                                ) => {
                                    handleDetailSupplementChange({
                                        IdSupplierHotelMealPlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='rate_plan'
                                disabled={
                                    !supplementDetail?.IdSupplierHotel ||
                                    supplierHotelRatePlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelRatePlans.data || []}
                                idValue={
                                    supplementDetail?.IdSupplierHotelRatePlan
                                }
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ISupplementComplementaryDataOption | null
                                ) => {
                                    handleDetailSupplementChange({
                                        IdSupplierHotelRatePlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <DateInput
                                labelText='from'
                                value={supplementDetail?.FromDate}
                                onChange={(date: Date | null) => {
                                    handleDetailSupplementChange({
                                        FromDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                maxDate={supplementDetail?.ToDate || undefined}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <DateInput
                                labelText='to'
                                value={supplementDetail?.ToDate}
                                onChange={(date: Date | null) => {
                                    handleDetailSupplementChange({
                                        ToDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                minDate={
                                    supplementDetail?.FromDate || undefined
                                }
                                error={!!formErrors.ToDate}
                                errorMessage={formErrors.ToDate}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                    </Grid>
                    <Grid
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing30}
                        style={{
                            borderBlock: '1px solid var(--border)',
                            marginBlock: '10px',
                            width: 'auto',
                        }}
                    >
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <IconRadioGroup
                                label={translate('calculation_type')}
                                value={
                                    supplementDetail?.IdSupplierTaxCalculationType ||
                                    0
                                }
                                onChange={value => {
                                    handleDetailSupplementChange({
                                        IdSupplierTaxCalculationType: parseInt(
                                            value.toString()
                                        ),
                                        IdCurrency: isFixedTaxId(
                                            parseInt(value.toString())
                                        )
                                            ? selectedSupplierHotel?.IdCurrency
                                            : supplementDetail?.IdCurrency || 0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationTypes.data || []
                                )
                                    .sort((a, b) => {
                                        const order =
                                            supplierTaxCalculationTypesOrder;
                                        if (a?.Key && b?.Key)
                                            return (
                                                order.indexOf(a?.Key) -
                                                order.indexOf(b?.Key)
                                            );

                                        return 0;
                                    })
                                    .map(
                                        (
                                            type: ISupplementComplementaryDataOption
                                        ) => ({
                                            value: parseInt(type.Id),
                                            label: type.Name,
                                            icon: type.Icon,
                                        })
                                    )}
                                error={
                                    !!formErrors.IdSupplierTaxCalculationType
                                }
                                errorMessage={
                                    formErrors.IdSupplierTaxCalculationType
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <IconRadioGroup
                                label={translate('supplement')}
                                info={<SupplementAdditionalInfo />}
                                value={
                                    supplementDetail?.IdSupplementCalculationType ||
                                    0
                                }
                                onChange={value => {
                                    handleDetailSupplementChange({
                                        IdSupplementCalculationType: parseInt(
                                            value.toString()
                                        ),
                                    });
                                }}
                                options={(supplementCalculationTypes.data || [])
                                    .sort((a, b) => {
                                        const order =
                                            supplementCalculationTypeOrder;
                                        if (a?.Key && b?.Key)
                                            return (
                                                order.indexOf(a?.Key) -
                                                order.indexOf(b?.Key)
                                            );

                                        return 0;
                                    })
                                    .map(
                                        (
                                            type: ISupplementComplementaryDataOption
                                        ) => ({
                                            value: parseInt(type.Id),
                                            label: type.Name,
                                            icon: type.Icon,
                                        })
                                    )}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.IdSupplementCalculationType}
                                errorMessage={
                                    formErrors.IdSupplementCalculationType
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <IconRadioGroup
                                label={translate('applies_by')}
                                value={ruleTypeAppliesBy}
                                onChange={value => {
                                    setRuleTypeAppliesBy(value.toString());
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationRuleTypesOptions.subjects ||
                                    []
                                ).map(subj => ({
                                    value: subj,
                                    label: subj,
                                    icon:
                                        supplierTaxCalculationRuleTypeSubjectIcons[
                                            subj as keyof typeof supplierTaxCalculationRuleTypeSubjectIcons
                                        ] ||
                                        UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_SUBJECT_TYPE,
                                }))}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.AppliesBy}
                                errorMessage={formErrors.AppliesBy}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={doubleMD_ColumnGrid}>
                            <IconRadioGroup
                                label={translate('frequency')}
                                value={ruleTypeFrequency}
                                onChange={value => {
                                    setRuleTypeFrequency(value.toString());
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationRuleTypesOptions.frequencies ||
                                    []
                                )
                                    .sort((a, b) => {
                                        const order =
                                            supplierTaxCalculationRuleTypeFrequencyOrder;
                                        if (a && b)
                                            return (
                                                order.indexOf(a) -
                                                order.indexOf(b)
                                            );

                                        return 0;
                                    })
                                    .map(subj => ({
                                        value: subj,
                                        label: subj,
                                        icon:
                                            supplierTaxCalculationRuleTypeFrequencyIcons[
                                                subj as keyof typeof supplierTaxCalculationRuleTypeFrequencyIcons
                                            ] ||
                                            UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_FREQUENCY_TYPE,
                                    }))}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.Frequency}
                                errorMessage={formErrors.Frequency}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        {isPerPersonRule ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <IconRadioGroup
                                    label={translate('age_segment')}
                                    value={appliesTo}
                                    onChange={value => {
                                        const updatedFields: Partial<ISupplementDetailState> =
                                            {
                                                OnlyAdults: false,
                                                OnlyChildren: false,
                                            };
                                        if (
                                            value ===
                                            SupplementAppliesTo.ONLY_ADULTS
                                        ) {
                                            updatedFields.OnlyAdults = true;
                                        }
                                        if (
                                            value ===
                                            SupplementAppliesTo.ONLY_CHILDREN
                                        ) {
                                            updatedFields.OnlyChildren = true;
                                        }
                                        handleDetailSupplementChange(
                                            updatedFields
                                        );
                                    }}
                                    options={appliesToOptions}
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                    </Grid>
                    <Grid
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing30}
                        style={{
                            width: 'auto',
                        }}
                    >
                        {isPerPersonRule ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <TextInput
                                    labelText='effective_person'
                                    value={supplementDetail?.EffectivePerson}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailSupplementChange({
                                            EffectivePerson: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                    helperText={
                                        <span className='flex gap-5 flex-align-start'>
                                            <Icon
                                                iconName='InfoOutlined'
                                                style={{
                                                    marginTop: '2px',
                                                    fontSize: '12px',
                                                }}
                                            />
                                            {translate(
                                                'effective_person_description'
                                            )}
                                        </span>
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        {isPerPersonRule ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <TextInput
                                    labelText='from_age'
                                    value={supplementDetail?.FromAge}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailSupplementChange({
                                            FromAge: parseInt(e.target.value),
                                        })
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        {isPerPersonRule ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <TextInput
                                    labelText='to_age'
                                    value={supplementDetail?.ToAge}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailSupplementChange({
                                            ToAge: parseInt(e.target.value),
                                        })
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}

                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <TextInput
                                labelText='amount'
                                value={supplementDetail?.Amount}
                                type='number'
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailSupplementChange({
                                        Amount: Number(e.target.value),
                                    })
                                }
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.Amount}
                                errorMessage={formErrors.Amount}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        {supplementDetail?.IdSupplierTaxCalculationType &&
                        isFixedTaxId(
                            supplementDetail?.IdSupplierTaxCalculationType
                        ) ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <LoadedOptionsTypeahead
                                    labelText='currency'
                                    options={currencies.data || []}
                                    idValue={supplementDetail?.IdCurrency}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: ISupplementComplementaryDataOption | null
                                    ) => {
                                        handleDetailSupplementChange({
                                            IdCurrency: value
                                                ? parseInt(value.Id)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (formErrors.isValidated) {
                                            handleValidateFields();
                                        }
                                    }}
                                    error={!!formErrors.IdCurrency}
                                    errorMessage={formErrors.IdCurrency}
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                            <TextInput
                                labelText='priority'
                                value={supplementDetail?.Priority.toString()}
                                type='number'
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailSupplementChange({
                                        Priority: parseInt(e.target.value),
                                    })
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={full_ColumnGrid}>
                            <TextInput
                                labelText='description2'
                                value={supplementDetail?.Description}
                                rows={5}
                                multiline
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailSupplementChange({
                                        Description: e.target.value,
                                    })
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                    </Grid>
                </main>
            )}
            <footer className='flex flex-between margin-block-16'>
                <Button
                    variant='contained'
                    className={'delete-button'}
                    disabled={isLoading || readOnlyUser}
                    onClick={() => {
                        handleDeleteSupplement();
                    }}
                >
                    {translate('delete')}
                </Button>
                <div className='flex gap-20'>
                    <Button
                        variant='outlined'
                        disabled={isLoading}
                        onClick={() => {
                            onClose();
                            handleResetPopUp();
                        }}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant='contained'
                        disabled={isLoading || readOnlyUser}
                        onClick={saveDDBBSupplement}
                    >
                        {translate('save')}
                    </Button>
                </div>
            </footer>
        </LateralPanel>
    );
};

export default SupplementPanel;
